<template>
    <div class="link-container" :class="variant">
        <div class="link-text" @click.prevent="handleClick">
            <slot />
        </div>
        <!-- <link-alert :variant="variant"> -->
        <alert v-if="alertMessage || variant === ComponentState.Timeout" :variant="variant" :size="ComponentVariant.Inline" class="alert">
            <template v-if="isCoolDownActive">
                <i18n-t keypath="common.link.countdown_timer" tag="div">
                    <template #seconds>
                        {{ countdownSeconds }}
                    </template>
                </i18n-t>
            </template>
            <template v-else>
                {{ alertMessage }}
            </template>
            <!-- </link-alert> -->
        </alert>
        <loading v-if="variant === ComponentState.Loading" class="link-loading" />
    </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import { ComponentState, ComponentVariant, Loading, Alert } from 'ihunter-pathfinder-components';

const emit = defineEmits(['click']);

const { t } = useI18n();

const variant = ref(ComponentState.Default);
const message = ref('');

const countdownSeconds = ref(0);
const isCoolDownActive = ref(false);

const alertMessage = computed(() => message.value);

const RESET_TIMEOUT_MS = 5000; // 5 seconds
const COOLDOWN_MS = 30000; // 30 seconds

let resetTimeout;
let countdownInterval;

const resetAfterTimeout = () => {
    clearTimeout(resetTimeout);

    resetTimeout = setTimeout(() => {
        variant.value = ComponentState.Default;
        message.value = '';
        if (!isCoolDownActive.value) {
            setGlobalCooldown(); // Starts cooldown AND sets isCoolDownActive = true
        }
    }, RESET_TIMEOUT_MS);
};

const setLinkState = (state, message) => {
    switch (state) {
        case ComponentState.Loading:
            setLoading();
            break;
        case ComponentState.Success:
            setSuccess(message);
            break;
        case ComponentState.Error:
            setError(message);
            break;
        case ComponentState.Default:
            reset();
            break;
        default:
            console.warn(`Unknown LinkState: ${state}`);
    }
};

const setLoading = () => {
    if (!isCoolDownActive.value) {
        clearTimeout(resetTimeout);
        variant.value = ComponentState.Loading;
        message.value = '';
    }
};

const setSuccess = (msg) => {
    if (isLoading()) {
        variant.value = ComponentState.Success;
        message.value = msg;
        resetAfterTimeout();
    }
};

const setError = (msg) => {
    if (isLoading()) {
        variant.value = ComponentState.Error;
        message.value = msg;
        resetAfterTimeout();
    }
};

const setGlobalCooldown = () => {
    //clearTimeout(resetTimeout);
    clearInterval(countdownInterval); // Ensure previous interval is cleared

    isCoolDownActive.value = true;
    countdownSeconds.value = Math.ceil(COOLDOWN_MS / 1000); // Start countdown

    countdownInterval = setInterval(() => {
        countdownSeconds.value--;
        console.log(countdownSeconds.value);
        if (countdownSeconds.value <= 0) {
            clearInterval(countdownInterval);
            isCoolDownActive.value = false;

            if (variant.value === ComponentState.Timeout) {
                variant.value = ComponentState.Default;
                message.value = '';
            }
        }
    }, 1000);
};

const reset = () => {
    if (!isLoading()) {
        clearTimeout(resetTimeout);
        clearInterval(countdownInterval);
        variant.value = ComponentState.Default;
        message.value = '';
    }
};

const isLoading = () => {
    return variant.value === ComponentState.Loading;
};

const handleClick = () => {
    if (isCoolDownActive.value === true) {
        variant.value = ComponentState.Timeout;
    }
    if (variant.value !== ComponentState.Default) return;
    emit('click');
};

defineExpose({ setLinkState });
</script>

<style lang="scss" scoped>
@use 'ihunter-pathfinder/styles' as Pathfinder;

.link-container {
    display: flex;
    height: Pathfinder.$space-300;
    @include Pathfinder.Macro-Text;
    width: max-content;
    gap: Pathfinder.$space-150;
    user-select: none;
    align-items: center;

    .link-text {
        text-decoration: underline;
        color: var(--action);
        cursor: pointer;
    }

    .alert {
        &:empty {
            display: none;
        }
    }

    .link-loading {
        display: flex;
        height: Pathfinder.$space-200;
        width: max-content;
        fill: var(--on-surface-alt);

        &:empty {
            display: none;
        }
    }

    &.success .link-text,
    &.error .link-text,
    &.timeout .link-text,
    &.loading .link-text {
        text-decoration: none;
        color: var(--on-surface-alt);
        cursor: pointer;
        pointer-events: none;
    }
}
</style>
